import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

// Configuración del tema Material-UI
const theme = createTheme({
  palette: {
    primary: {
      main: '#000000',        // Negro
      contrastText: '#ffeb5d', // Texto amarillo sobre negro
    },
    secondary: {
      main: '#ffeb5d',        // Amarillo como secundario
      contrastText: '#000000', // Texto negro sobre amarillo
    },
    background: {
      default: '#f5f5f5',     // Gris claro mantenido
    },
    text: {
      primary: '#000000',     // Texto principal negro
      secondary: '#666666',   // Texto secundario gris oscuro
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700, // Bold - Títulos muy grandes
    },
    h2: {
      fontWeight: 700, // Bold - Títulos grandes
    },
    h3: {
      fontWeight: 600, // Semi-bold - Títulos medianos
    },
    h4: {
      fontWeight: 600, // Semi-bold - Títulos de página
    },
    h5: {
      fontWeight: 600, // Semi-bold - Subtítulos importantes
    },
    h6: {
      fontWeight: 600, // Semi-bold - Títulos de sección
    },
    subtitle1: {
      fontWeight: 500, // Medium - Subtítulos
    },
    subtitle2: {
      fontWeight: 500, // Medium - Subtítulos secundarios
    },
    body1: {
      fontWeight: 400, // Regular - Texto normal
    },
    body2: {
      fontWeight: 400, // Regular - Texto secundario
    },
    button: {
      fontWeight: 600, // Semi-bold - Botones destacados
      letterSpacing: '0.5px',
    },
    caption: {
      fontWeight: 300, // Light - Textos auxiliares pequeños
    },
    overline: {
      fontWeight: 500, // Medium - Etiquetas superiores
      letterSpacing: '1px',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100vw',
        },
        body: {
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100vw',
        },
        '#root': {
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100vw',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
        containedPrimary: {
          backgroundColor: '#ffeb5d',  // Amarillo
          color: '#000000',            // Texto negro
          '&:hover': {
            backgroundColor: '#ffe135', // Amarillo más oscuro en hover
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
        startIcon: {
          '& > svg': {
            overflow: 'visible', // Asegurar que el SVG no se corte
          }
        }
      },
    },
  },
});

// Cliente de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0, // Sin caché - DESARROLLO
      cacheTime: 0, // No cachear - DESARROLLO
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);